import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { courierAPI } from "../../utils/api";
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Download,
  Star,
  Package,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num)
    ? "0.00"
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const formatNumber = (val) => {
  const num =
    typeof val === "string"
      ? parseInt(val, 10)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0" : num.toLocaleString("en-US");
};

const StatCard = ({ title, value, icon: Icon, color = "blue", subtitle }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    yellow: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-violet-50 text-violet-600 border-violet-100",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-100",
  };
  return (
    <div
      className={`card border ${colors[color]} hover:shadow-md transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2 truncate">
            {value}
          </p>
          {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div
          className={`p-3 rounded-xl flex-shrink-0 ${colors[color].replace("border-", "bg-").replace("50", "100")}`}
        >
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ label, value, max = 100, color = "bg-emerald-500" }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className="text-sm font-bold text-gray-900">{value}%</span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
      <div
        className={`${color} h-2.5 rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(value, max)}%` }}
      ></div>
    </div>
  </div>
);

const CourierEarnings = () => {
  const [period, setPeriod] = useState(30);

  const {
    data: earningsData,
    isLoading: earningsLoading,
    error: earningsError,
  } = useQuery({
    queryKey: ["courierEarnings", period],
    queryFn: () => courierAPI.earnings(period),
    retry: 2,
  });

  const { data: performanceData, isLoading: perfLoading } = useQuery({
    queryKey: ["courierPerformance"],
    queryFn: () => courierAPI.performance(),
    retry: 2,
  });

  const earnings = earningsData?.data?.data || {};
  const performance = performanceData?.data?.data || {};

  const isLoading = earningsLoading || perfLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (earningsError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load earnings</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Earnings</h1>
          <p className="text-gray-500 mt-1">
            Track your income and performance metrics
          </p>
        </div>
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => setPeriod(days)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === days
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {days === 7 ? "Week" : days === 30 ? "Month" : "3 Months"}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Earnings"
          value={`EGP ${formatMoney(earnings.total)}`}
          icon={DollarSign}
          color="green"
          subtitle={`${formatNumber(earnings.total_deliveries)} deliveries`}
        />
        <StatCard
          title="Avg Per Delivery"
          value={`EGP ${formatMoney(earnings.average_per_delivery)}`}
          icon={Package}
          color="blue"
        />
        <StatCard
          title="Average Rating"
          value={`${performance.average_rating || 0}/5`}
          icon={Star}
          color="yellow"
          subtitle={`${formatNumber(performance.total_reviews)} reviews`}
        />
        <StatCard
          title="On-Time Rate"
          value={`${performance.on_time_rate || 0}%`}
          icon={Clock}
          color="cyan"
          subtitle={`${formatNumber(performance.early_deliveries)} early`}
        />
      </div>

      {/* Earnings Chart */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
            Earnings Breakdown
          </h3>
          <button className="text-sm text-gray-500 hover:text-gray-700 flex items-center transition-colors">
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </button>
        </div>
        {earnings.breakdown && earnings.breakdown.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={earnings.breakdown}>
              <defs>
                <linearGradient id="earnGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip
                formatter={(value) => [`$${value}`, "Earnings"]}
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Area
                type="monotone"
                dataKey="earnings"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#earnGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex flex-col items-center justify-center text-gray-400">
            <Calendar className="w-12 h-12 text-gray-300 mb-3" />
            <p className="font-medium">No earnings data for this period</p>
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
            Performance Metrics
          </h3>
          <div className="space-y-6">
            <ProgressBar
              label="Customer Satisfaction"
              value={performance.customer_satisfaction || 0}
              color="bg-emerald-500"
            />
            <ProgressBar
              label="On-Time Delivery"
              value={performance.on_time_rate || 0}
              color="bg-blue-500"
            />
            <ProgressBar
              label="Order Acceptance Rate"
              value={performance.acceptance_rate || 0}
              color="bg-violet-500"
            />
            <ProgressBar
              label="Completion Rate"
              value={performance.completion_rate || 0}
              color="bg-amber-500"
            />
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-emerald-500" />
            Quick Stats
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(performance.total_deliveries)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Deliveries</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(performance.this_month_deliveries)}
              </p>
              <p className="text-sm text-gray-500 mt-1">This Month</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(performance.early_deliveries)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Early Deliveries</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(performance.late_deliveries)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Late Deliveries</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourierEarnings;
